import React, { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Upload, Play, Download, Loader2, CheckCircle, AlertCircle, RotateCcw, Mic, AudioLines, FolderOpen,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useCredits, CREDIT_OPERATIONS } from '@/hooks/useCredits';
import { useCreditConfirm } from '@/components/credits/CreditConfirmDialog';
import { UpgradeModal } from './subscription';
import { LoginModal } from './LoginModal';
import { ToolShell, StepCard, StepHeader, type ToolStep } from './tool-shell';
import LibraryPicker, { probeVideoUrl, stagedFileName, type StagedLibraryFile } from './library/LibraryPicker';
import { useNavigate } from 'react-router-dom';

type WizardStep = 'upload' | 'enhance' | 'process' | 'download';

const STEPS: { key: WizardStep; label: string }[] = [
  { key: 'upload', label: 'Upload' },
  { key: 'enhance', label: 'Enhance' },
  { key: 'process', label: 'Process' },
  { key: 'download', label: 'Download' },
];

const STEP_ORDER: WizardStep[] = ['upload', 'enhance', 'process', 'download'];

interface VideoInfo {
  file: File;
  name: string;
  objectUrl: string;
  s3Key: string;
  s3Url: string;
  duration: number;
  status: 'uploading' | 'converting' | 'uploaded' | 'error';
  uploadProgress?: number;
}

export const AudioEnhancerTool = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getAuthHeaders, getAuthToken } = useAuth();
  const { isSubscriber, loading: subscriptionLoading } = useSubscription();
  const { checkCredits } = useCredits();
  const { confirmCredit, CreditConfirmDialog } = useCreditConfirm();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  const [completedSteps, setCompletedSteps] = useState<Set<WizardStep>>(new Set());

  // Upload state
  const [video, setVideo] = useState<VideoInfo | null>(null);

  // Enhance state
  const [audioEnhanceMode, setAudioEnhanceMode] = useState<'light' | 'natural' | 'strong'>('natural');
  const [audioPreviewLoading, setAudioPreviewLoading] = useState(false);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);
  const [audioPreviewMode, setAudioPreviewMode] = useState<'light' | 'natural' | 'strong' | null>(null);

  // Process state
  const [processStatus, setProcessStatus] = useState<'idle' | 'processing' | 'completed' | 'failed'>('idle');
  const [processError, setProcessError] = useState('');

  // Download state
  const [finalVideoUrl, setFinalVideoUrl] = useState('');

  // Track paid credit operations so restarts don't re-charge
  const [creditsPaidFor, setCreditsPaidFor] = useState<Set<string>>(new Set());

  const previewVideoRef = useRef<HTMLVideoElement>(null);

  // Credit confirmation helper
  const isCreditUser = useCallback(() => !!getAuthToken(), [getAuthToken]);

  const requireCreditConfirmation = useCallback(async (
    operationType: string,
    operationName: string,
    onConfirm: () => void
  ) => {
    // Skip credit check if already paid in this session
    if (creditsPaidFor.has(operationType)) { onConfirm(); return; }
    if (!isCreditUser()) { onConfirm(); return; }
    const creditCheck = await checkCredits(operationType);
    if (creditCheck?.isFree) { onConfirm(); return; }
    if (!subscriptionLoading && !isSubscriber) { setShowUpgradeModal(true); return; }
    if (creditCheck?.hasCredits) {
      confirmCredit(operationType as any, operationName, () => {
        setCreditsPaidFor(prev => new Set([...prev, operationType]));
        onConfirm();
      });
    } else {
      toast({ title: 'Not enough video tokens', description: 'Please purchase more video tokens to continue.', variant: 'destructive' });
    }
  }, [isCreditUser, checkCredits, isSubscriber, subscriptionLoading, confirmCredit, toast, creditsPaidFor]);

  // ===== NAVIGATION =====
  const stepIndex = STEP_ORDER.indexOf(currentStep);

  const advanceStep = () => {
    const nextIndex = stepIndex + 1;
    if (nextIndex < STEP_ORDER.length) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(STEP_ORDER[nextIndex]);
    }
  };

  const resetFromEnhance = () => {
    setAudioPreviewUrl(null);
    setAudioPreviewMode(null);
    setProcessStatus('idle');
    setProcessError('');
    setFinalVideoUrl('');
    setCompletedSteps(prev => {
      const next = new Set(prev);
      next.delete('enhance');
      next.delete('process');
      next.delete('download');
      return next;
    });
  };

  const resetAll = () => {
    if (video) URL.revokeObjectURL(video.objectUrl);
    setVideo(null);
    setAudioEnhanceMode('natural');
    resetFromEnhance();
    setCompletedSteps(new Set());
    setCurrentStep('upload');
  };

  const goToStep = (step: WizardStep) => {
    const targetIndex = STEP_ORDER.indexOf(step);
    if (targetIndex > stepIndex && !completedSteps.has(STEP_ORDER[targetIndex - 1])) return;
    // Don't allow navigating away from process while a job is in flight
    if (currentStep === 'process' && processStatus === 'processing') return;
    if (targetIndex < stepIndex) {
      if (step === 'upload' || step === 'enhance') resetFromEnhance();
    }
    setCurrentStep(step);
  };

  const onShellBack = () => {
    if (stepIndex > 0) goToStep(STEP_ORDER[stepIndex - 1]);
  };

  // ===== STEP 1: UPLOAD =====
  const uploadFileToS3 = async (file: File) => {
    try {
      const res = await fetch('/get-upload-url', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ fileName: file.name, fileType: file.type || 'video/mp4', ephemeral: true }),
      });
      if (!res.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, key, downloadUrl } = await res.json();

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setVideo(prev => prev ? { ...prev, uploadProgress: pct } : prev);
          }
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(file);
      });

      return { key, downloadUrl };
    } catch (err: any) {
      setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
      toast({ title: 'Upload failed', description: `${file.name}: ${err.message}`, variant: 'destructive' });
      return null;
    }
  };

  // Non-MP4 → MP4 conversion via AWS MediaConvert (handles HDR→SDR tone mapping)
  const convertMovClip = async (s3Key: string) => {
    try {
      const res = await fetch('/api/convert-mov', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ s3Key }),
      });
      if (!res.ok) throw new Error('Failed to start conversion');
      const { jobId } = await res.json();
      pollConversion(jobId);
    } catch (err: any) {
      setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
      toast({ title: 'Conversion failed', description: err.message, variant: 'destructive' });
    }
  };

  const pollConversion = async (jobId: string) => {
    const poll = async () => {
      try {
        const res = await fetch(`/api/convert-mov/status/${jobId}`);
        if (!res.ok) throw new Error('Failed to check conversion status');
        const data = await res.json();

        if (data.status === 'completed') {
          // Load metadata from the converted MP4 (Chrome can read H.264)
          const tempVideo = document.createElement('video');
          tempVideo.preload = 'metadata';
          tempVideo.src = data.downloadUrl;
          let metadataHandled = false;
          const updateAsUploaded = (withMetadata: boolean) => {
            if (metadataHandled) return;
            metadataHandled = true;
            setVideo(prev => prev ? {
              ...prev,
              s3Key: data.outputKey,
              s3Url: data.downloadUrl,
              objectUrl: `/api/s3/hey-joe-video-rotator/${data.outputKey}`,
              ...(withMetadata ? { duration: tempVideo.duration } : {}),
              status: 'uploaded' as const,
            } : prev);
          };
          tempVideo.onloadedmetadata = () => updateAsUploaded(true);
          tempVideo.onerror = () => updateAsUploaded(false);
          // Fallback: if neither event fires within 10s, update anyway
          setTimeout(() => updateAsUploaded(false), 10000);
        } else if (data.status === 'failed') {
          setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
          toast({ title: 'Conversion failed', description: data.error, variant: 'destructive' });
        } else {
          // Still processing — poll again in 5 seconds
          setTimeout(poll, 5000);
        }
      } catch (err: any) {
        setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
        toast({ title: 'Conversion error', description: err.message, variant: 'destructive' });
      }
    };
    poll();
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles.find(f =>
      f.type.startsWith('video/') || ['.mp4', '.mov', '.webm', '.mkv', '.avi'].some(ext => f.name.toLowerCase().endsWith(ext))
    );
    if (!file) {
      toast({ title: 'Invalid file', description: 'Please select a video file.', variant: 'destructive' });
      return;
    }

    if (video) URL.revokeObjectURL(video.objectUrl);
    resetFromEnhance();

    const objectUrl = URL.createObjectURL(file);
    setVideo({
      file,
      name: file.name,
      objectUrl,
      s3Key: '',
      s3Url: '',
      duration: 0,
      status: 'uploading',
      uploadProgress: 0,
    });

    // Detect duration from the local file
    const tempVideo = document.createElement('video');
    tempVideo.preload = 'metadata';
    tempVideo.src = objectUrl;
    tempVideo.onloadedmetadata = () => {
      setVideo(prev => prev ? { ...prev, duration: tempVideo.duration } : prev);
    };

    const result = await uploadFileToS3(file);
    if (result) {
      const needsConversion = !file.name.toLowerCase().endsWith('.mp4');
      if (needsConversion) {
        // Non-MP4 files (MOV, WebM, AVI, MKV) need MediaConvert conversion to normalized MP4
        setVideo(prev => prev ? { ...prev, s3Key: result.key, s3Url: result.downloadUrl, status: 'converting' as const } : prev);
        convertMovClip(result.key);
      } else {
        setVideo(prev => prev ? { ...prev, s3Key: result.key, s3Url: result.downloadUrl, status: 'uploaded' as const } : prev);
      }
    }
  }, [video, getAuthHeaders, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'] },
    multiple: false,
  });

  // "Choose from your library" — a staged library file slots in exactly like a
  // completed upload (the staged key already lives in the ephemeral tool bucket).
  const openLibraryPicker = () => {
    if (!getAuthToken()) { setShowLoginModal(true); return; }
    setShowLibraryPicker(true);
  };

  const handleLibraryPick = async (f: StagedLibraryFile) => {
    const fileName = stagedFileName(f);
    if (video) URL.revokeObjectURL(video.objectUrl);
    resetFromEnhance();
    const base: VideoInfo = {
      file: new File([], fileName, { type: 'video/mp4' }),
      name: f.name,
      objectUrl: f.downloadUrl,
      s3Key: f.key,
      s3Url: f.downloadUrl,
      duration: 0,
      status: 'uploaded',
    };
    if (!fileName.toLowerCase().endsWith('.mp4')) {
      // Non-MP4 library files go through the same MediaConvert path as uploads
      setVideo({ ...base, status: 'converting' });
      convertMovClip(f.key);
      return;
    }
    setVideo(base);
    try {
      const meta = await probeVideoUrl(f.downloadUrl);
      setVideo(prev => prev ? { ...prev, duration: meta.duration } : prev);
    } catch { /* duration is best-effort here */ }
  };

  // ===== STEP 2: ENHANCE (preview) =====
  // 10-second audio preview so users can A/B the modes before committing.
  const previewAudioEnhancement = async () => {
    const sourceUrl = video?.s3Url;
    const sourceKey = video?.s3Key;
    if (!sourceUrl && !sourceKey) {
      toast({ title: 'No audio available to preview yet', variant: 'destructive' });
      return;
    }
    setAudioPreviewLoading(true);
    setAudioPreviewUrl(null);
    setAudioPreviewMode(audioEnhanceMode);
    try {
      const authToken = getAuthToken();
      const startRes = await fetch('/api/audio-enhance/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}) },
        body: JSON.stringify({
          videoUrl: sourceUrl,
          videoKey: sourceKey,
          sourceBucket: 'hey-joe-video-rotator',
          mode: audioEnhanceMode,
          startTime: 0,
        }),
      });
      if (!startRes.ok) throw new Error('Preview request failed');
      const { jobId } = await startRes.json();

      // Poll up to 3 minutes (a 10s clip is usually <1 min)
      let done = false;
      for (let i = 0; i < 60 && !done; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const sRes = await fetch(`/api/audio-enhance/preview/status/${jobId}`, {
          headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {},
        });
        if (!sRes.ok) continue;
        const sData = await sRes.json();
        if (sData.status === 'completed' && sData.audioUrl) {
          setAudioPreviewUrl(sData.audioUrl);
          done = true;
        } else if (sData.status === 'failed') {
          throw new Error(sData.error || 'Preview failed');
        }
      }
      if (!done) throw new Error('Preview timed out');
    } catch (err: any) {
      toast({ title: 'Preview failed', description: err.message, variant: 'destructive' });
      setAudioPreviewMode(null);
    } finally {
      setAudioPreviewLoading(false);
    }
  };

  // ===== STEP 3: PROCESS =====
  const startAudioEnhancement = async (videoUrl: string) => {
    setProcessStatus('processing');
    setProcessError('');
    try {
      const authToken = getAuthToken();
      const res = await fetch('/api/enhance-video-audio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}) },
        body: JSON.stringify({ videoUrl, mode: audioEnhanceMode }),
      });
      if (!res.ok) throw new Error('Audio enhancement failed');
      const { jobId } = await res.json();
      toast({ title: 'Enhancing audio', description: 'Improving audio quality...' });

      let attempts = 0;
      const interval = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await fetch(`/api/enhance-video-audio/status/${jobId}`);
          const statusData = await statusRes.json();
          if (statusData.status === 'completed' && statusData.videoUrl) {
            clearInterval(interval);
            finishProcessing(statusData.videoUrl);
            toast({ title: 'Audio enhanced', description: 'Your video is ready to download.' });
          } else if (statusData.status === 'failed') {
            clearInterval(interval);
            setProcessStatus('failed');
            setProcessError(statusData.error || 'Audio enhancement failed.');
            toast({ title: 'Audio enhancement failed', description: statusData.error || 'Please try again.', variant: 'destructive' });
          } else if (attempts >= 240) {
            clearInterval(interval);
            setProcessStatus('failed');
            setProcessError('Audio enhancement timed out.');
            toast({ title: 'Audio enhancement timed out', variant: 'destructive' });
          }
        } catch {
          if (attempts >= 240) {
            clearInterval(interval);
            setProcessStatus('failed');
            setProcessError('Audio enhancement timed out.');
          }
        }
      }, 5000);
    } catch (err: any) {
      setProcessStatus('failed');
      setProcessError(err.message);
      toast({ title: 'Audio enhancement failed', description: err.message, variant: 'destructive' });
    }
  };

  const finishProcessing = (url: string) => {
    setFinalVideoUrl(url);
    setProcessStatus('completed');
    setCompletedSteps(prev => new Set([...prev, 'enhance', 'process']));
    setCurrentStep('download');
  };

  // Advance from the Enhance step — credit-confirm, then kick off the job
  const handleStartEnhancement = () => {
    if (!video?.s3Url) {
      toast({ title: 'No video uploaded', description: 'Upload a video first.', variant: 'destructive' });
      return;
    }
    // Paid enhancement requires login
    if (!getAuthToken()) {
      setShowLoginModal(true);
      return;
    }
    requireCreditConfirmation(CREDIT_OPERATIONS.AUDIO_ENHANCEMENT, 'Audio Enhancement', () => {
      setCompletedSteps(prev => new Set([...prev, 'enhance']));
      setCurrentStep('process');
      startAudioEnhancement(video.s3Url);
    });
  };

  // ===== STEP 4: DOWNLOAD =====
  const getDownloadFileName = () => {
    if (!video) return 'enhanced_audio.mp4';
    const baseName = video.name.replace(/\.[^.]+$/, ''); // Strip original extension
    return `enhanced_${baseName}.mp4`;
  };

  const handleDownload = async () => {
    const fileName = getDownloadFileName();
    const isIOS = /iPhone|iPad/i.test(navigator.userAgent);

    // iOS: use Web Share API so user gets "Save Video" option for camera roll
    if (isIOS && navigator.share) {
      try {
        const resp = await fetch(finalVideoUrl);
        if (resp.ok) {
          const blob = await resp.blob();
          const file = new File([blob], fileName, { type: 'video/mp4' });
          if (navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file], title: fileName });
            return;
          }
        }
      } catch {
        // Share cancelled or failed — fall through to URL-based share
      }

      try {
        await navigator.share({ url: finalVideoUrl, title: fileName });
        return;
      } catch {
        // User cancelled — fall through to direct download
      }
    }

    // Determine the fetch URL — proxy URLs get download=1 for Content-Disposition
    let fetchUrl = finalVideoUrl;
    if (finalVideoUrl.includes('/api/s3/')) {
      const separator = finalVideoUrl.includes('?') ? '&' : '?';
      fetchUrl = `${finalVideoUrl}${separator}download=1&filename=${encodeURIComponent(fileName)}`;
    }

    // Desktop / Android: fetch blob and trigger download
    try {
      const resp = await fetch(fetchUrl);
      if (!resp.ok) throw new Error(`${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    } catch {
      // Fetch failed (CORS or 404) — fall through to direct navigation
    }

    // Fallback: direct navigation
    if (finalVideoUrl.includes('/api/s3/')) {
      window.location.href = fetchUrl;
    } else {
      window.open(finalVideoUrl, '_blank');
    }
  };

  // ===== PER-STEP ADVANCE CONFIG =====
  const currentStepAdvance: {
    onAdvance: () => void;
    label?: string;
    disabled?: boolean;
    showAdvance?: boolean;
  } = (() => {
    switch (currentStep) {
      case 'upload':
        return {
          onAdvance: advanceStep,
          label: video?.status === 'converting' ? 'Converting…' : video?.status === 'uploading' ? 'Uploading…' : 'Continue to Enhance',
          disabled: !video || video.status !== 'uploaded',
        };
      case 'enhance':
        return {
          onAdvance: handleStartEnhancement,
          label: 'Enhance audio · 1 video token',
        };
      case 'process': {
        if (processStatus === 'completed') {
          return { onAdvance: advanceStep, label: 'Continue to Download' };
        }
        if (processStatus === 'failed') {
          return {
            onAdvance: () => { setProcessStatus('idle'); setProcessError(''); setCurrentStep('enhance'); },
            label: 'Try again',
          };
        }
        return { onAdvance: () => {}, label: 'Enhancing audio…', disabled: true };
      }
      case 'download':
        // Terminal step — workspace owns its actions (Save to Device, Start Over)
        return { onAdvance: () => {}, showAdvance: false };
      default:
        return { onAdvance: advanceStep };
    }
  })();

  // ===== RENDER UI =====
  const renderUploadStep = () => (
    <StepCard>
      <StepHeader
        kicker="Step 01 · Upload"
        title="Drop in your video."
        subtitle="One clip — we'll clean up the sound and hand back the same video with studio-grade audio."
      />

      {/* Drop card */}
      <div
        {...getRootProps()}
        className="flex flex-col justify-between cursor-pointer transition-colors"
        style={{
          padding: '18px 20px',
          minHeight: 140,
          borderRadius: 'var(--hj-r-md)',
          border: `1.5px dashed ${isDragActive ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
          background: isDragActive ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
          gap: 14,
        }}
      >
        <input {...getInputProps()} />
        <div className="flex items-center justify-between gap-3">
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{
              width: 40,
              height: 40,
              borderRadius: 'var(--hj-r-sm)',
              background: 'var(--hj-surface-3)',
              color: 'var(--hj-ink-2)',
            }}
          >
            <Upload size={18} strokeWidth={1.8} />
          </div>
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--hj-ink)', marginBottom: 2 }}>
            Drop or browse a clip
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.4 }}>
            MP4 · MOV · WebM · AVI · MKV — one file.
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={openLibraryPicker}
        className="inline-flex items-center gap-2"
        style={{
          marginTop: 10,
          padding: '8px 12px',
          borderRadius: 'var(--hj-r-sm)',
          border: '1px solid var(--hj-hairline)',
          background: 'transparent',
          color: 'var(--hj-ink-2)',
          fontSize: 12.5,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        <FolderOpen size={14} strokeWidth={1.8} />
        Choose from your library
      </button>

      {video && (
        <div style={{ marginTop: 18 }}>
          {/* Status row */}
          <div
            className="flex items-center justify-between gap-3"
            style={{
              padding: '12px 14px',
              borderRadius: 'var(--hj-r-sm)',
              background: 'var(--hj-surface-2)',
              border: '1px solid var(--hj-hairline-soft)',
              marginBottom: 14,
            }}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 'var(--hj-r-sm)',
                  background: 'var(--hj-surface-3)',
                  color: 'var(--hj-ink-2)',
                }}
              >
                <AudioLines size={15} strokeWidth={1.8} />
              </div>
              <span className="truncate" style={{ fontSize: 13.5, color: 'var(--hj-ink)', fontWeight: 500 }}>
                {video.name}
              </span>
            </div>
            <span className="mono-label flex-shrink-0">
              {video.status === 'uploading' && `Uploading ${video.uploadProgress ?? 0}%`}
              {video.status === 'converting' && 'Converting…'}
              {video.status === 'uploaded' && 'Ready'}
              {video.status === 'error' && 'Failed'}
            </span>
          </div>

          {(video.status === 'uploading' || video.status === 'converting') && (
            <div
              style={{
                height: 6,
                borderRadius: 999,
                background: 'var(--hj-surface-3)',
                overflow: 'hidden',
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: video.status === 'converting' ? '100%' : `${video.uploadProgress ?? 0}%`,
                  background: 'var(--hj-blue)',
                  transition: 'width 0.25s ease',
                  opacity: video.status === 'converting' ? 0.5 : 1,
                }}
              />
            </div>
          )}

          {/* Preview tile */}
          <div
            className="aspect-video overflow-hidden relative"
            style={{
              background: 'oklch(0.08 0.005 70)',
              borderRadius: 'var(--hj-r-md)',
              border: '1px solid var(--hj-hairline)',
            }}
          >
            <video
              src={video.objectUrl}
              className="w-full h-full object-contain"
              controls
              preload="metadata"
              playsInline
            />
          </div>
        </div>
      )}
    </StepCard>
  );

  const renderEnhanceStep = () => {
    const audioModes = [
      { k: 'light',   label: 'Light',   desc: 'Levels only. No denoise — keeps the original recording intact.' },
      { k: 'natural', label: 'Natural', desc: 'Classic denoise. Preserves voice character.' },
      { k: 'strong',  label: 'Strong',  desc: 'AI isolation + dereverb. Best for noisy/echoey rooms.' },
    ] as const;
    return (
      <StepCard>
        <StepHeader
          kicker="Step 02 · Enhance"
          title="Fix the sound."
          subtitle="Studio-grade voice cleanup — de-noise, de-reverb, level. Preview a 10s sample before you commit."
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {audioModes.map((m) => {
            const active = audioEnhanceMode === m.k;
            return (
              <button
                key={m.k}
                type="button"
                onClick={() => {
                  setAudioEnhanceMode(m.k as typeof audioEnhanceMode);
                  setAudioPreviewUrl(null);
                  setAudioPreviewMode(null);
                }}
                className="text-left transition-colors"
                style={{
                  padding: 12,
                  borderRadius: 'var(--hj-r-sm)',
                  background: active ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
                  border: `1px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
                  color: 'var(--hj-ink)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <div style={{ fontSize: 13.5, fontWeight: 500 }}>{m.label}</div>
                <div style={{ fontSize: 11.5, color: 'var(--hj-ink-2)', marginTop: 2, lineHeight: 1.4 }}>
                  {m.desc}
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3" style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={previewAudioEnhancement}
            disabled={audioPreviewLoading || !video}
            className="inline-flex items-center gap-1.5 transition-opacity disabled:cursor-not-allowed"
            style={{
              padding: '7px 14px',
              borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--hj-hairline)',
              color: 'var(--hj-ink-2)',
              fontSize: 12.5,
              fontFamily: 'inherit',
              cursor: audioPreviewLoading || !video ? 'not-allowed' : 'pointer',
              opacity: audioPreviewLoading || !video ? 0.5 : 1,
            }}
          >
            {audioPreviewLoading ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing preview…</>
            ) : (
              <><Play className="w-3.5 h-3.5" /> Preview 10s sample</>
            )}
          </button>
          {audioPreviewUrl && audioPreviewMode === audioEnhanceMode && (
            <span className="mono-label" style={{ color: 'var(--hj-green)' }}>Preview ready</span>
          )}
          {audioPreviewMode && audioPreviewMode !== audioEnhanceMode && audioPreviewUrl && (
            <span className="mono-label">Mode changed — re-preview</span>
          )}
        </div>

        {audioPreviewUrl && audioPreviewMode === audioEnhanceMode && (
          <audio
            controls
            src={audioPreviewUrl}
            className="w-full"
            style={{ marginTop: 10, height: 40 }}
          />
        )}
      </StepCard>
    );
  };

  const renderProcessStep = () => (
    <StepCard>
      <StepHeader
        kicker="Step 03 · Process"
        title="Joe's working."
        subtitle="Cleaning up your audio and remuxing it into the video."
      />

      {/* Settings summary */}
      <div
        style={{
          padding: '14px 16px',
          borderRadius: 'var(--hj-r-sm)',
          background: 'var(--hj-surface-2)',
          border: '1px solid var(--hj-hairline-soft)',
          marginBottom: 16,
        }}
      >
        <div className="mono-label" style={{ marginBottom: 8 }}>Enhancement</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <span style={{ fontSize: 12, color: 'var(--hj-ink-4)' }}>Clip</span>
            <span className="truncate text-right" style={{ fontSize: 13, color: 'var(--hj-ink-2)', fontWeight: 500 }}>
              {video?.name || '—'}
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <span style={{ fontSize: 12, color: 'var(--hj-ink-4)' }}>Mode</span>
            <span className="text-right" style={{ fontSize: 13, color: 'var(--hj-ink-2)', fontWeight: 500 }}>
              {audioEnhanceMode}
            </span>
          </div>
        </div>
      </div>

      {processStatus === 'processing' && (
        <div
          style={{
            padding: '20px 22px',
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-2)',
            border: '1px solid var(--hj-hairline-soft)',
            marginBottom: 14,
          }}
        >
          <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
            <div className="flex items-center gap-2.5">
              <span
                aria-hidden
                className="rounded-full"
                style={{
                  width: 10,
                  height: 10,
                  background: 'var(--hj-blue)',
                  boxShadow: '0 0 12px var(--hj-blue)',
                  animation: 'pulse 1.4s ease-in-out infinite',
                }}
              />
              <span style={{ fontSize: 14.5, color: 'var(--hj-ink)', fontWeight: 500 }}>
                Enhancing audio
              </span>
            </div>
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />
          </div>
          <div
            style={{
              height: 6,
              borderRadius: 999,
              background: 'var(--hj-surface-3)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: '100%',
                background: 'var(--hj-blue)',
                opacity: 0.5,
              }}
            />
          </div>
          <div className="flex items-center justify-between" style={{ marginTop: 12 }}>
            <span className="mono-label">Don't close the tab</span>
            <span className="mono-label">1 video token</span>
          </div>
          <p
            style={{
              marginTop: 14,
              paddingTop: 12,
              borderTop: '1px solid var(--hj-hairline-soft)',
              fontSize: 12,
              color: 'var(--hj-ink-2)',
              lineHeight: 1.5,
            }}
          >
            Audio enhancement usually takes about a minute per minute of video.
            Hang tight — your clip will move to Download automatically when it's done.
          </p>
        </div>
      )}

      {processStatus === 'failed' && (
        <div
          style={{
            padding: 16,
            borderRadius: 'var(--hj-r-sm)',
            border: '1px solid hsl(var(--destructive) / 0.5)',
            background: 'hsl(var(--destructive) / 0.08)',
          }}
        >
          <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
            <AlertCircle className="w-5 h-5" style={{ color: 'hsl(var(--destructive))' }} />
            <p style={{ fontWeight: 600, color: 'hsl(var(--destructive))', margin: 0 }}>Audio enhancement failed</p>
          </div>
          {processError && (
            <p style={{ fontSize: 13, color: 'hsl(var(--destructive) / 0.85)', margin: 0, lineHeight: 1.4 }}>
              {processError}
            </p>
          )}
        </div>
      )}

      {processStatus === 'completed' && (
        <div
          className="flex items-center gap-3"
          style={{
            padding: '14px 16px',
            borderRadius: 'var(--hj-r-sm)',
            background: 'oklch(0.42 0.15 145 / 0.12)',
            border: '1px solid var(--hj-green)',
            color: 'var(--hj-ink)',
          }}
        >
          <CheckCircle className="w-5 h-5" style={{ color: 'var(--hj-green)' }} />
          <div>
            <div style={{ fontWeight: 600 }}>Audio enhanced</div>
            <div style={{ fontSize: 12, color: 'var(--hj-ink-2)', marginTop: 2 }}>
              Continue to download your video.
            </div>
          </div>
        </div>
      )}
    </StepCard>
  );

  const renderDownloadStep = () => (
    <StepCard>
      <StepHeader
        kicker="Step 04 · Download"
        title="Sound's fixed."
        subtitle="Save your video with the enhanced audio, or start a new clip."
      />

      {finalVideoUrl ? (
        <>
          {/* Big preview tile */}
          <div
            className="aspect-video overflow-hidden relative"
            style={{
              background: 'oklch(0.08 0.005 70)',
              borderRadius: 'var(--hj-r-md)',
              border: '1px solid var(--hj-hairline)',
              marginBottom: 16,
            }}
          >
            <video
              ref={previewVideoRef}
              src={finalVideoUrl}
              className="w-full h-full object-contain"
              controls
              preload="auto"
              playsInline
            />
          </div>

          {/* Primary action row */}
          <div className="flex flex-col sm:flex-row gap-2.5">
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center justify-center gap-2 transition-opacity"
              style={{
                padding: '12px 22px',
                borderRadius: 999,
                background: 'var(--hj-blue)',
                border: '1px solid var(--hj-blue)',
                color: 'oklch(0.18 0.005 70)',
                fontSize: 15,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 48,
              }}
            >
              <Download className="w-4 h-4" />
              Save to device
            </button>
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center justify-center gap-2"
              style={{
                padding: '12px 22px',
                borderRadius: 999,
                background: 'transparent',
                border: '1px solid var(--hj-hairline)',
                color: 'var(--hj-ink-2)',
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 48,
              }}
            >
              <RotateCcw className="w-4 h-4" />
              Start over
            </button>
          </div>
        </>
      ) : (
        <div
          className="flex items-center gap-3"
          style={{
            padding: 16,
            borderRadius: 'var(--hj-r-sm)',
            background: 'var(--hj-surface-2)',
            border: '1px solid var(--hj-hairline-soft)',
          }}
        >
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />
          <span style={{ fontSize: 13.5, color: 'var(--hj-ink-2)' }}>
            Waiting for the enhanced video…
          </span>
        </div>
      )}
    </StepCard>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'upload': return renderUploadStep();
      case 'enhance': return renderEnhanceStep();
      case 'process': return renderProcessStep();
      case 'download': return renderDownloadStep();
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--hj-bg)' }}>
      <ToolShell
        toolName="Audio Enhancer"
        toolNumber={`Tool · ${String(stepIndex + 1).padStart(2, '0')} / ${String(STEPS.length).padStart(2, '0')}`}
        usesCredits
        steps={STEPS as ToolStep[]}
        currentStepKey={currentStep}
        completedKeys={completedSteps as Set<string>}
        onStepChange={(k) => goToStep(k as WizardStep)}
        onBack={onShellBack}
        onAdvance={currentStepAdvance.onAdvance}
        advanceLabel={currentStepAdvance.label}
        advanceDisabled={currentStepAdvance.disabled}
        showAdvance={currentStepAdvance.showAdvance !== false}
        onBackToStudio={() => navigate('/')}
      >
        {renderCurrentStep()}
      </ToolShell>

      <CreditConfirmDialog />
      <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} trigger="ai_feature" />
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <LibraryPicker open={showLibraryPicker} onClose={() => setShowLibraryPicker(false)} onPick={handleLibraryPick} />
    </div>
  );
};
